/**
 * Free-shipping add-ons row — Something Splendid Co.
 * ---------------------------------------------------------------------------
 * Markup, product pool and styles: snippets/cart-addons.liquid.
 *
 * How it stays in sync without touching theme.js:
 *   - Impulse rebuilds the drawer's [data-products] from the server after
 *     every add / quantity change / remove. We watch that node; when it
 *     changes we read /cart.js once and re-render. The drawer was just
 *     rebuilt from the same server state, so the two cannot disagree.
 *   - Adding an item: POST /cart/add.js, then dispatch `cart:build`, which is
 *     Impulse's own "rebuild the drawer" event (verified 16 Sep 2026). No
 *     <form> is used, so theme.js's global add-to-cart interceptor and the
 *     Metizsoft gift-box flow never see these adds.
 *
 * Buttons are type="button": the whole drawer is one <form>, and a default
 * button would submit it to checkout.
 */
(function () {
  'use strict';
  if (window.__ssAddons) return;
  window.__ssAddons = true;

  var MAX_TILES = 3;
  var root, row, pool, threshold;
  var pending = null;
  var busy = false;

  function money(cents) {
    if (window.SSFreeShippingBar && window.SSFreeShippingBar.formatMoney) {
      return window.SSFreeShippingBar.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function readPool() {
    var el = document.querySelector('[data-ss-addons-pool]');
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Pick up to 3.
   * - If any item closes the gap in one add, those come first, cheapest
   *   first: the smallest purchase that unlocks free shipping.
   * - Every remaining slot follows the collection's own order, so the
   *   merchandising set in Shopify admin (seasonal first, etc.) decides.
   * One per vendor, so three near-identical journals never fill the row.
   */
  function choose(cart) {
    var gap = threshold - cart.total_price;
    var inCart = {};
    (cart.items || []).forEach(function (item) { inCart[item.product_id] = true; });

    var candidates = pool.filter(function (p) { return !inCart[p.product]; });

    var closes = candidates.filter(function (p) { return p.price >= gap; })
      .sort(function (a, b) { return a.price - b.price; });
    var rest = candidates.filter(function (p) { return p.price < gap; });

    var picked = [];
    var vendors = {};
    closes.concat(rest).some(function (p) {
      if (vendors[p.vendor]) return false;
      vendors[p.vendor] = true;
      picked.push(p);
      return picked.length >= MAX_TILES;
    });

    return { gap: gap, items: picked };
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function render(cart) {
    if (!root || !row) return;

    if (!cart || !cart.item_count || cart.total_price >= threshold) {
      root.hidden = true;
      row.innerHTML = '';
      return;
    }

    var choice = choose(cart);
    if (!choice.items.length) {
      root.hidden = true;
      row.innerHTML = '';
      return;
    }

    // If every tile unlocks free shipping, say it once in the heading
    // instead of three identical badges. Mixed rows badge the tiles.
    var allUnlock = choice.items.every(function (p) { return p.price >= choice.gap; });
    var title = root.querySelector('.ss-addons__title');
    if (title) title.textContent = allUnlock ? 'Add any one to ship free' : 'Little extras';

    row.innerHTML = choice.items.map(function (p) {
      var unlocks = !allUnlock && p.price >= choice.gap;
      return (
        '<div class="ss-addon">' +
          // Image is not wrapped in a link: keeps the tile to one tap target
          // for the product page (the name) and one for Add.
          (p.image
            ? '<img class="ss-addon__img" src="' + escapeHtml(p.image) + '" alt="" width="180" height="180">'
            : '<span class="ss-addon__img"></span>') +
          '<a href="' + escapeHtml(p.url) + '" class="ss-addon__name">' + escapeHtml(p.title) + '</a>' +
          '<div class="ss-addon__meta">' +
            '<span class="ss-addon__price">' + money(p.price) + '</span>' +
            '<button type="button" class="ss-addon__add" data-ss-addon-variant="' + p.variant + '"' +
              ' aria-label="Add ' + escapeHtml(p.title) + ' to cart">+ Add</button>' +
          '</div>' +
          (unlocks ? '<div class="ss-addon__badge">Unlocks free shipping</div>' : '') +
        '</div>'
      );
    }).join('');

    root.hidden = false;
  }

  function refresh() {
    if (pending) return pending;
    pending = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) { render(cart); })
      .catch(function () { /* leave whatever is showing */ })
      .then(function () { pending = null; });
    return pending;
  }

  var refreshTimer = null;
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 120);
  }

  function add(button) {
    if (busy) return;
    var variant = parseInt(button.getAttribute('data-ss-addon-variant'), 10);
    if (!variant) return;

    busy = true;
    var label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variant, quantity: 1, properties: { _source: 'cart-addon' } }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        // Impulse's own event: rebuilds [data-products], subtotal and the
        // free-shipping bar. Our observer then re-renders this row.
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function () {
        button.textContent = 'Unavailable';
        setTimeout(scheduleRefresh, 1200);
      })
      .then(function () {
        busy = false;
        if (button.isConnected && button.textContent === 'Adding…') {
          button.disabled = false;
          button.textContent = label;
        }
      });
  }

  function init() {
    root = document.querySelector('#CartDrawer [data-ss-addons]');
    if (!root || root.__ssInit) return;
    root.__ssInit = true;

    row = root.querySelector('[data-ss-addons-row]');
    threshold = parseInt(root.getAttribute('data-threshold'), 10) || 0;
    pool = readPool();
    if (!threshold || !pool.length) return;

    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-ss-addon-variant]');
      if (!btn) return;
      e.preventDefault();
      add(btn);
    });

    var products = document.querySelector('#CartDrawer [data-products]');
    if (products && typeof MutationObserver === 'function') {
      new MutationObserver(scheduleRefresh).observe(products, { childList: true });
    }
    ['cart:open', 'cart:updated', 'ajaxProduct:added'].forEach(function (name) {
      document.addEventListener(name, scheduleRefresh);
    });

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
